import {
    ABSENTEE_BID_FAILURE,
    ABSENTEE_BID_FAILURE_ACTION,
    ABSENTEE_BID_REQUEST,
    ABSENTEE_BID_REQUEST_ACTION,
    ABSENTEE_BID_SUCCESS,
    ABSENTEE_BID_SUCCESS_ACTION,
    DISMISS_MODAL,
    OPEN_ABSENTEE_BID_MODAL,
    OPEN_ABSENTEE_BID_MODAL_ACTION,
} from './actions';
import { createSelector, Reducer } from '@reduxjs/toolkit';
import { fetchBidLimitBalanceIfNeeded } from './bidLimit';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getPlacedAbsenteeBidAnalytics, trackConversionForAlgolia } from './analytics';
import { GlobalState } from '../rootReducer';
import { handleActions } from 'redux-actions';
import { isUiMobile } from './browser';
import { postPlaceAbsenteeBid } from '@/redux/api/bid';

export interface State {
    readonly bidAmount: number | null;
    readonly bidPlaced: boolean;
    readonly error: boolean;
    readonly isCurrentHighBid: boolean;
    readonly itemId: number | null;
    readonly submitted: boolean;
    readonly success: boolean;
}

export const DEFAULT_STATE: State = {
    bidAmount: null,
    bidPlaced: false,
    error: false,
    isCurrentHighBid: false,
    itemId: null,
    submitted: false,
    success: false,
};

export const reducer: Reducer<State> = handleActions(
    {
        [ABSENTEE_BID_FAILURE]: (state: State) => ({
            ...state,
            bidPlaced: false,
            error: true,
            submitted: false,
            success: false,
        }),
        [ABSENTEE_BID_REQUEST]: (state: State) => ({
            ...state,
            bidPlaced: true,
            error: false,
            submitted: true,
            success: false,
        }),
        [ABSENTEE_BID_SUCCESS]: (state: State, action: ABSENTEE_BID_SUCCESS_ACTION) => ({
            ...state,
            bidAmount: null,
            error: false,
            isCurrentHighBid: action.payload.isCurrentHighBid,
            submitted: false,
            success: true,
        }),
        [DISMISS_MODAL]: (state: State) => ({
            ...state,
            bidPlaced: false,
        }),
        [OPEN_ABSENTEE_BID_MODAL]: (state: State, action: OPEN_ABSENTEE_BID_MODAL_ACTION) => ({
            ...state,
            bidAmount: action.payload.bidAmount || null,
            itemId: action.payload.itemId,
        }),
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.absenteeBid;

export const getUiAbsenteeBid = createSelector(stateSelector, (state) => state);

export const getAbsenteeBidAmount = createSelector(getUiAbsenteeBid, (uiState) => {
    return !isNaN(uiState.bidAmount) ? uiState.bidAmount : null;
});

export const getAbsenteeBidItemId = createSelector(getUiAbsenteeBid, (uiState) => {
    return uiState.itemId ? uiState.itemId : null;
});

/* ACTION CREATORS */
export const submitAbsenteeBid = (details: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        const uiMobile = isUiMobile(state);
        const isMobile = uiMobile === 'tablet' || uiMobile === 'phone';

        details.client = 'React';
        details.deviceType = isMobile ? 'Mobile' : 'Desktop';

        dispatch({
            type: ABSENTEE_BID_REQUEST,
        } as ABSENTEE_BID_REQUEST_ACTION);

        const response = await postPlaceAbsenteeBid({ authToken, deployment, details });
        const analytics = getPlacedAbsenteeBidAnalytics(state, details.itemId, details.bidAmount);
        dispatch(trackConversionForAlgolia(details.itemId));

        dispatch(fetchBidLimitBalanceIfNeeded({ catalogId: details.catalogId, force: true, itemId: details.itemId }));

        dispatch({
            meta: {
                actionTime: Date.now(),
                analytics,
                itemId: details.itemId,
            },
            payload: response,
            type: ABSENTEE_BID_SUCCESS,
        } as ABSENTEE_BID_SUCCESS_ACTION);
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: ABSENTEE_BID_FAILURE,
        } as ABSENTEE_BID_FAILURE_ACTION);
    }
};
